import { apiClient } from "../client";

/** Astrologer record as returned by /astrologers and /astrologers/filter. */
export interface Astrologer {
  id: number;
  name: string;
  display_name: string;
  /** Per-session price as a decimal string, e.g. "1100.00". */
  rate: string;
  /** Session duration in minutes. */
  duration: number;
  image_url: string | null;
  /** Experience in years. */
  experience: number;
  rating: number | null;
  reviews_count: number | null;
  /** Skill display names, e.g. "Face Reading". */
  skills: string[];
  /** Language display names, e.g. "Hindi". */
  languages: string[];
  /** Only present on the /filter endpoint. */
  online_status?: "online" | "offline" | string;
}

/** One consultation's money row from /astrologer/earnings. Every figure is
 *  server-computed: GST comes out of the gross rate first, then the platform's
 *  commission (set per-platform by the admin) off the GST-exclusive base. */
export interface AstrologerEarningRow {
  booking_id: number;
  booking_code: string;
  date: string;
  status: string;
  consultation_type: string;
  duration: number | null;
  customer: { id: number | null; name: string | null };
  rate: number;
  gst_percent: number;
  gst: number;
  taxable_value: number;
  commission_percent: number;
  commission: number;
  net_earning: number;
  /** true once the consultation completed and its figures were frozen. */
  settled: boolean;
}

export interface AstrologerEarningsSummary {
  bookings: number;
  gross: number;
  gst: number;
  taxable: number;
  commission: number;
  net: number;
}

/** The rates currently in force — what a screen should label its columns with,
 *  rather than assuming a percentage of its own. */
export interface AstrologerEarningsRates {
  percent: number;
  gst_percent: number;
  is_active: boolean;
  /** true when this astrologer is on a rate of their own. */
  overridden: boolean;
  /** true for the house astrologer when the admin has exempted him from the
   *  platform's commission entirely — `percent` is then 0. */
  house_exempt: boolean;
}

export interface AstrologerEarningsResponse {
  success: boolean;
  message?: string;
  data: {
    rows: AstrologerEarningRow[];
    summary: AstrologerEarningsSummary;
    rates: AstrologerEarningsRates;
    astrologer: { id: number; name: string };
  };
}

export interface AstrologerEarningsParams {
  /** "completed" (default) is earned money; "active" adds what is still ahead,
   *  "all" includes cancelled bookings. A concrete status also works. */
  status?: "completed" | "active" | "all" | string;
  /** YYYY-MM-DD, inclusive. */
  from?: string;
  to?: string;
}

export interface AstrologerListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/**
 * Live /astrologers/filter contract (differs from the Postman doc, verified
 * against production): expertise/languages match display NAMES, not slugs.
 */
export interface AstrologerFilterParams {
  search?: string;
  /** Skill names, e.g. ["Face Reading"]. */
  expertise?: string[];
  /** Language names, e.g. ["Hindi"]. */
  languages?: string[];
  min_rate?: number;
  max_rate?: number;
  min_experience?: number;
  max_experience?: number;
  sort_by?: "rating" | "rate" | "experience";
  sort_order?: "asc" | "desc";
  page?: number;
  per_page?: number;
}

export interface AstrologerFilterResponse {
  status: boolean;
  message?: string;
  data: Astrologer[];
  meta?: AstrologerListMeta;
}

export interface AstrologerListResponse {
  status: boolean;
  data: Astrologer[];
}

/** One weekly consultation window in an onboarding application. */
export interface AvailabilitySlot {
  day: string;
  slots: Array<{ from: string; to: string }>;
}

/** POST /astrologers body — the astrologer onboarding application. */
export interface AstrologerRegistrationPayload {
  first_name: string;
  last_name: string;
  display_name: string;
  short_intro: string;
  details_bio: string;
  address: string;
  state_id: number | string;
  city_id: number | string;
  pin_code: string;
  consultation_mode: string;
  ac_holder_name: string;
  bank_name: string;
  ac_number: string;
  ifsc_code: string;
  branch_name: string;
  upi_id: string;
  applicant_name: string;
  email: string;
  mobile_no: string;
  password: string;
  experience: number | null;
  rate: number | null;
  duration: number | null;
  languages: Array<number | string>;
  skills: Array<number | string>;
  education: Array<{ degree: string; institution: string; year: number | null }>;
  aadhar_number: string;
  pan_number: string;
  /** Signature as a data-URL (base64 PNG). */
  astrologer_signature_image: string;
  availabilities: AvailabilitySlot[];
}

export interface AstrologerRegistrationResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
}

/**
 * PUT /astrologer/profile body — the astrologer editing their own profile
 * from the dashboard. Upstream validates every field with `sometimes`, so
 * everything is optional and only the keys sent are validated/updated. Same
 * field vocabulary as registration minus the one-time application fields
 * (password, applicant_name, signature).
 */
export type AstrologerProfileUpdatePayload = Partial<
  Omit<
    AstrologerRegistrationPayload,
    "password" | "applicant_name" | "astrologer_signature_image"
  >
>;

/** PATCH /astrologer/pricing-slabs body — tiered per-session prices in ₹. */
/**
 * POST /astrologer/onboarding body — the SAME application as registration, for
 * an account that already exists (the dashboard's "Become an Astrologer"
 * screen). The account fields are omitted: upstream attaches the profile to the
 * signed-in user, so email/mobile/password are never re-sent.
 */
export type AstrologerOnboardingPayload = Omit<
  AstrologerRegistrationPayload,
  "email" | "mobile_no" | "password"
>;

/** The caller's astrologer application, once submitted. */
export interface AstrologerApplication {
  id: number;
  slug: string | null;
  name: string;
  /** Review state: pending until an admin reviews it in KYC Review. */
  status: "pending" | "approved" | "rejected" | string;
  /** Approved AND switched on — i.e. actually bookable by seekers. */
  is_live: boolean;
  submitted_at: string;
  reviewed_at: string;
}

/** GET /astrologer/onboarding payload. */
export interface AstrologerOnboardingStatus {
  /** Whether the admin has the prompt on for this account at all. */
  visible: boolean;
  /** Which rule decided it (audience_all, override_deny, disabled…). */
  reason: string;
  /** The one field the button needs: offered AND not already applied. */
  can_apply: boolean;
  /** Non-null once an application exists — then the screen reports its state. */
  application: AstrologerApplication | null;
}

export interface AstrologerOnboardingResponse {
  success?: boolean;
  message?: string;
  data?: AstrologerOnboardingStatus;
}

export interface PricingSlabsPayload {
  slab_1_price: number;
  slab_2_price: number;
  slab_3_price: number;
  is_active: boolean;
}

export interface PricingSlabsResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
}

export const astrologers = {
  /** Plain paginated listing (no online_status). */
  list: (params: { page?: number; per_page?: number } = {}) =>
    apiClient.get<AstrologerListResponse>("/astrologers", { params: { ...params } }),

  /**
   * Filtered listing — also the richest read (includes online_status), so the
   * UI uses it even with no filters applied.
   */
  filter: (params: AstrologerFilterParams = {}) =>
    apiClient.get<AstrologerFilterResponse>("/astrologers/filter", { params: { ...params } }),

  /** Single astrologer by numeric id or slug. */
  get: (idOrSlug: number | string) => apiClient.get<unknown>(`/astrologers/${idOrSlug}`),

  /**
   * Submit an astrologer onboarding application (upstream /v1/astrologers).
   * Pass a FormData to include uploads: the same fields in bracket notation
   * plus files under upstream's media keys — `photo`, `aadhar_document`,
   * `pan_document` (one file each; `education_documents` takes several) —
   * which the backend stores in S3 object storage. The Content-Type override
   * stops axios JSON-encoding the FormData (the client default is JSON) and
   * lets the browser set the multipart boundary itself.
   */
  register: (payload: AstrologerRegistrationPayload | FormData) =>
    apiClient.post<AstrologerRegistrationResponse>("/astrologers", payload, {
      headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
    }),

  /**
   * Is this signed-in account offered the "Become an Astrologer" prompt, and
   * where is its application up to? Admin-controlled (Feature Visibility in the
   * admin panel), so the answer differs per account and is never cached long.
   */
  onboarding: () => apiClient.get<AstrologerOnboardingResponse>("/astrologer/onboarding"),

  /**
   * Submit the astrologer application for the signed-in account
   * (POST /astrologer/onboarding, auth-guarded). Same multipart contract as
   * `register` — bracket-notation fields plus files under `photo`,
   * `aadhar_document`, `pan_document` — minus the account fields, which
   * upstream takes from the session.
   */
  applyOnboarding: (payload: AstrologerOnboardingPayload | FormData) =>
    apiClient.post<AstrologerOnboardingResponse>("/astrologer/onboarding", payload, {
      headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
    }),

  /** The signed-in astrologer's own full profile (GET /astrologer/profile,
   * Sanctum-guarded) — 404 if the user has no linked astrologer record. */
  profile: () => apiClient.get<unknown>("/astrologer/profile"),

  /** Update the signed-in astrologer's own profile (PUT /astrologer/profile,
   * Sanctum-guarded). Partial: only the keys sent are validated/updated. */
  updateProfile: (payload: AstrologerProfileUpdatePayload) =>
    apiClient.put<AstrologerRegistrationResponse>("/astrologer/profile", payload),

  /** The signed-in astrologer's per-booking earnings breakdown
   * (GET /astrologer/earnings, auth-guarded): rows, totals, and the commission
   * and GST rates they were costed at. Defaults to completed consultations —
   * pass status "active"/"all" for the wider view. */
  earnings: (params: AstrologerEarningsParams = {}) =>
    apiClient.get<AstrologerEarningsResponse>("/astrologer/earnings", { params: { ...params } }),

  /** Pricing slabs (GET /astrologer/pricing-slabs). Sanctum-guarded when
   * called with no args — resolves to the signed-in astrologer, used to
   * prefill the dashboard Settings tab. Public callers (e.g. the unauth'd
   * Maharaj booking page) pass `astrologer_id` to look up a specific
   * astrologer's slabs instead. */
  pricingSlabs: (params: { astrologer_id?: number | string } = {}) =>
    apiClient.get<PricingSlabsResponse>("/astrologer/pricing-slabs", { params }),

  /** Update the signed-in astrologer's pricing slabs
   * (PATCH /astrologer/pricing-slabs, Sanctum-guarded). */
  updatePricingSlabs: (payload: PricingSlabsPayload) =>
    apiClient.patch<PricingSlabsResponse>("/astrologer/pricing-slabs", payload),
};
